import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, X, Store, ShieldCheck, LogOut, Package, Moon, Sun } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { SeasonalSaleBanner } from "@/components/SeasonalSaleBanner";
import { useCurrency } from "@/context/CurrencyContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const { cartCount, wishlist } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { currency, change: changeCurrency, currencies } = useCurrency();
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [shopTheme, setShopTheme] = useState(localStorage.getItem("mk_shop_theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("mk-dark", shopTheme === "dark");
  }, [shopTheme]);

  const toggleShopTheme = () => {
    const next = shopTheme === "dark" ? "light" : "dark";
    setShopTheme(next);
    localStorage.setItem("mk_shop_theme", next);
  };

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/products?search=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 backdrop-saturate-150 border-b border-black/5">
      <div className="bg-[#0A0A0A] text-white text-center text-xs py-1.5 tracking-wide" data-testid="top-banner">
        UK-WIDE DELIVERY · FREE SHIPPING OVER £50 · VAT INCLUDED
      </div>
      <SeasonalSaleBanner />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img src="/logo-full.png" alt="MRQK UK Digital Services Marketplace logo" className="h-10 w-auto object-contain" data-testid="site-logo" />
          </Link>

          <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-xl items-center border border-neutral-300 focus-within:border-[#0047AB] transition-colors">
            <input
              data-testid="search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, brands, categories…"
              className="flex-1 px-4 py-2.5 text-sm outline-none bg-transparent"
            />
            <button data-testid="search-submit" type="submit" className="px-4 py-2.5 bg-[#0047AB] text-white hover:bg-[#003380] transition-colors">
              <Search className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>

          <div className="flex items-center gap-1 sm:gap-3">
            <select
              value={currency}
              onChange={(e) => changeCurrency(e.target.value)}
              className="hidden sm:block text-sm border border-neutral-200 px-2 py-1.5 outline-none focus:border-[#0047AB] cursor-pointer bg-transparent"
              data-testid="currency-select"
            >
              {Object.values(currencies).map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <button onClick={toggleShopTheme} data-testid="shop-theme-toggle" className="p-2 hover:bg-neutral-100 transition-colors" title={shopTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
              {shopTheme === "dark" ? <Sun className="w-5 h-5" strokeWidth={1.5} /> : <Moon className="w-5 h-5" strokeWidth={1.5} />}
            </button>
            <Link to="/wishlist" data-testid="wishlist-link" className="relative p-2 hover:bg-neutral-100 transition-colors">
              <Heart className="w-5 h-5" strokeWidth={1.5} />
              {wishlist.length > 0 && <span className="absolute top-0 right-0 bg-[#E63946] text-white text-[10px] w-4 h-4 flex items-center justify-center">{wishlist.length}</span>}
            </Link>
            <Link to="/cart" data-testid="cart-link" className="relative p-2 hover:bg-neutral-100 transition-colors">
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cartCount > 0 && <span className="absolute top-0 right-0 bg-[#0047AB] text-white text-[10px] w-4 h-4 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="p-2 hover:bg-neutral-100 transition-colors flex items-center gap-2">
                    <User className="w-5 h-5" strokeWidth={1.5} />
                    <span className="hidden lg:inline text-sm max-w-[100px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none w-52">
                  <div className="px-2 py-1.5 text-xs text-neutral-500 truncate">{user.email}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}><Package className="w-4 h-4 mr-2" />My Account</DropdownMenuItem>
                  {(user.role === "vendor") && <DropdownMenuItem data-testid="menu-vendor" onClick={() => navigate("/vendor")}><Store className="w-4 h-4 mr-2" />Vendor Dashboard</DropdownMenuItem>}
                  {(user.role === "vendor_staff") && <DropdownMenuItem data-testid="menu-team" onClick={() => navigate("/team")}><Store className="w-4 h-4 mr-2" />Team Dashboard</DropdownMenuItem>}
                  {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><ShieldCheck className="w-4 h-4 mr-2" />Admin Panel</DropdownMenuItem>}
                  {user.role === "customer" && <DropdownMenuItem data-testid="menu-sell" onClick={() => navigate("/vendor")}><Store className="w-4 h-4 mr-2" />Sell on MRQK</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="login-link" className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0047AB] text-white text-sm hover:bg-[#003380] transition-colors">
                <User className="w-4 h-4" strokeWidth={1.5} /> Sign in
              </Link>
            )}
            <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-4 h-11 text-sm whitespace-nowrap overflow-x-auto [scrollbar-width:none]">
          <Link to="/products" className="hover:text-[#0047AB] transition-colors font-medium" data-testid="nav-all">All Products</Link>
          {cats.map((c) => (
            <Link key={c.id} to={`/products?category=${encodeURIComponent(c.name)}`} className="text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid={`nav-cat-${c.slug}`}>{c.name}</Link>
          ))}
          <Link to="/templates" className="text-neutral-600 hover:text-[#0047AB] transition-colors flex items-center gap-1" data-testid="nav-templates">Templates</Link>
          <Link to="/cards" className="text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid="nav-cards">Cards</Link>
          <Link to="/live" className="text-neutral-600 hover:text-[#E63946] transition-colors flex items-center gap-1.5" data-testid="nav-live"><span className="w-1.5 h-1.5 rounded-full bg-[#E63946] animate-pulse" />Live</Link>
          <Link to="/community" className="text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid="nav-community">Community</Link>
          <Link to="/sell" className="text-[#00875A] hover:text-[#0047AB] transition-colors font-medium" data-testid="nav-sell">Sell on MRQK</Link>
          <Link to="/workplace-documents" className="text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid="nav-documents">Documents</Link>
          <Link to="/mrqk-templates" className="text-[#0E7490] hover:text-[#0047AB] transition-colors font-medium" data-testid="nav-mrqk-templates">Sheets & Planners</Link>
          <Link to="/wall-art" className="text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid="nav-wall-art">Prints</Link>
          <Link to="/brand-studio" className="text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid="nav-brand-studio">Brand&nbsp;Studio</Link>
          <Link to="/about" className="text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid="nav-about">About</Link>
          <Link to="/track" className="ml-auto text-neutral-600 hover:text-[#0047AB] transition-colors" data-testid="nav-track">Track Order</Link>
        </nav>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-200 p-4 space-y-3 bg-white" data-testid="mobile-menu">
          <form onSubmit={doSearch} className="flex border border-neutral-300">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 px-3 py-2 text-sm outline-none" />
            <button type="submit" className="px-3 bg-[#0047AB] text-white"><Search className="w-4 h-4" /></button>
          </form>
          <Link to="/products" onClick={() => setOpen(false)} className="block py-1">All Products</Link>
          {cats.map((c) => <Link key={c.id} to={`/products?category=${encodeURIComponent(c.name)}`} onClick={() => setOpen(false)} className="block py-1 text-neutral-600">{c.name}</Link>)}
          <Link to="/track" onClick={() => setOpen(false)} className="block py-1 text-neutral-600">Track Order</Link>
          <Link to="/community" onClick={() => setOpen(false)} className="block py-1 text-neutral-600">Community</Link>
          <Link to="/sell" onClick={() => setOpen(false)} className="block py-1 text-[#00875A] font-medium">Sell on MRQK</Link>
          <Link to="/workplace-documents" onClick={() => setOpen(false)} className="block py-1 text-neutral-600" data-testid="mobile-nav-documents">Documents</Link>
          <Link to="/mrqk-templates" onClick={() => setOpen(false)} className="block py-1 text-[#0E7490] font-medium" data-testid="mobile-nav-mrqk-templates">Sheets & Planners</Link>
          <Link to="/wall-art" onClick={() => setOpen(false)} className="block py-1 text-neutral-600" data-testid="mobile-nav-wall-art">Wall Art</Link>
          <Link to="/brand-studio" onClick={() => setOpen(false)} className="block py-1 text-neutral-600" data-testid="mobile-nav-brand-studio">Brand Studio</Link>
          {!user && <Link to="/login" onClick={() => setOpen(false)} className="block py-2 bg-[#0047AB] text-white text-center">Sign in</Link>}
        </div>
      )}
    </header>
  );
};
